import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ChangeDetectorRef, Component, DestroyRef, inject, Inject, OnInit, signal } from '@angular/core';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { firstValueFrom, from, Observable, of, startWith, switchMap } from 'rxjs';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { Store } from '@ngxs/store';
import { ConsumerEntityService, ConsumerEntityState } from '../../consumer/services/consumer.entity.service';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import { normalizeManyRelations, normalizeOneRelation } from '../../core/decorators/relation.decorator';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { map } from 'rxjs/operators';
import {
  MultiComboEntitySearchComponent
} from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDivider } from '@angular/material/divider';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { LearningUnitEntityService } from '../../learning-unit/services/learning-unit.entity.service';
import { ModuleEntity } from '../module.entity';
import { StateFilterCBComponent } from '../../utils/component/state-filter-combobox/state-cb.component';
import { SchoolClassEntity } from '../../core/global-enums/school-class/school-class.entity';
import { SchoolClassEntityService } from '../../core/global-enums/school-class/services/school-class.entity.service';
import { EntityStateModel } from '../../core/state/entity.state';
import { MatIcon } from '@angular/material/icon';
import { AuthState } from '../../core/authentication/auth.state';
import { EditorRole } from '../../editor/editor.role.enum';
import { MediaType } from '../../core/types';
import { ChooseMedium } from '../../utils/component/choose-medium/choose-medium.json.model';
import { ChooseMediumComponent } from '../../utils/component/choose-medium/choose-medium.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { AssetJsonModel } from '../../asset/assets.json.model';
import { environment } from '../../../environments/environment';
import { ConfirmDialogComponent } from '../../core/components/confirm-delete-dialog/confirm-dialog.component';
import {
  ObjectConsumerMetadataComponent
} from '../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import {
  GlobalMetaDataEntityService
} from '../../global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataEntity, MetadataField } from '../../global-metadata/global-metadata.entity';
import {
  ModuleLearningUnitMetadataEntityService
} from '../services/module-learning-unit-metadata.entity.service';
import { ModuleLearningUnitMetadataEntity } from '../module-learning-unit-metadata.entity';

export interface MetadataRow {
  id?: string
  cantonKey: string
  professionKey: string
  learningUnitIds: string[]
}

export interface MetadataSelectOption {
  id: string
  label: string
}

@Component({
  selector: 'pixl-form-dialog-module',
  templateUrl: './form-dialog-module.component.html',
  imports: [
    CommonModule,
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatFormField,
    MatInput,
    MatFormField,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    MatSelect,
    AsyncPipe,
    MatOption,
    FormsModule,
    MultiComboEntitySearchComponent,
    CdkDrag,
    CdkDropList,
    MatDivider,
    StateFilterCBComponent,
    MatIcon,
    MatIconButton,
    MatTabGroup,
    MatTab,
    ObjectConsumerMetadataComponent
  ],
})
export class FormDialogModuleComponent implements OnInit {
  form: FormGroup;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  learningUnits$!: Observable<LearningUnitEntity[]>;
  schoolClasses$!: Observable<SchoolClassEntity[]>;
  selectedConsumerName$!: Observable<string>;

  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private learningUnitService = inject(LearningUnitEntityService);
  private schoolClassService = inject(SchoolClassEntityService);
  private globalMetadataService = inject(GlobalMetaDataEntityService);
  private metadataEntityService = inject(ModuleLearningUnitMetadataEntityService);

  selectedLearningUnits: LearningUnitEntity[] = [];
  currentPageLearningUnits = 1;
  pageSizeLearningUnits = 20;
  searchQueryLearningUnits: string | null = null;

  public editor$ = this.store.select(AuthState.getEditor);
  public isZavAdmin = false;

  public imageUrl = signal('');

  status = '';

  // ABU metadata
  isAbuConsumer = false;
  cantonOptions: MetadataField[] = [];
  professionOptions: MetadataField[] = [];
  cantonSelectOptions: MetadataSelectOption[] = [];
  professionSelectOptions: MetadataSelectOption[] = [];
  metadataRowCantonOptions$: Observable<MetadataSelectOption[]>[] = [];
  metadataRowProfessionOptions$: Observable<MetadataSelectOption[]>[] = [];
  metadataRows: MetadataRow[] = [];
  allLearningUnits: LearningUnitEntity[] = [];
  metadataRowSelectedCantons: MetadataSelectOption[][] = [];
  metadataRowSelectedProfessions: MetadataSelectOption[][] = [];
  metadataRowLearningUnits$: Observable<LearningUnitEntity[]>[] = [];
  metadataRowSelectedLearningUnits: LearningUnitEntity[][] = [];
  metadataRowSearchQueries: (string | null)[] = [];
  metadataRowCurrentPages: number[] = [];
  metadataRowPageSize = 20;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<FormDialogModuleComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: Partial<ModuleEntity>,
  ) {
    this.form = this.fb.group({
      preTitle: [data?.preTitle || ''],
      title: [data?.title || ''],
      description: [data?.description || ''],
      status: [data?.status || 'Edit'],
      preview: [data?.preview || []],
      content: this.fb.array((data?.content || []).map(c => this.fb.control(c))),
      consumer: [normalizeOneRelation<ConsumerEntity>(data?.consumer)],
      subjects: [normalizeManyRelations(data?.subjects)],
      learningUnits: [normalizeManyRelations(data?.learningUnits)],
      schoolClasses: [normalizeManyRelations(data?.schoolClasses)],
      consumerData: this.fb.group(
        Object.fromEntries(
          Object.entries(data?.consumerData || {}).map(
            ([key, value]) => [key, Array.isArray(value) ? [value] : value]
          )
        )
      ),
    });
    this.buildURLs();
  }

  ngOnInit() {
    const learningUnitQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };

    this.learningUnits$ = from(
      this.learningUnitService.ensureLoaded(learningUnitQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.learningUnitService.allByQuery(learningUnitQuery)).pipe(
          map(paginated => {
            return [...this.selectedLearningUnits, ...paginated].filter(
              (lu, index, self) => index === self.findIndex(s => s.id === lu.id)
            );
          })
        )
      )
    );

    const selectedLearningUnitIds = normalizeManyRelations(this.data?.learningUnits) as string[];

    const state = this.store.selectSnapshot(this.learningUnitService.stateClass) as EntityStateModel<LearningUnitEntity>;
    this.selectedLearningUnits = selectedLearningUnitIds
      .map(id => state.entities[id])
      .filter((e): e is LearningUnitEntity => !!e);

    this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitQuery)).pipe(
      map(paginated => {
        return [...this.selectedLearningUnits, ...paginated].filter(
          (seq, index, self) => index === self.findIndex(s => s.id === seq.id)
        );
      })
    );

    this.form.get('learningUnits')?.setValue(this.selectedLearningUnits.map(s => s.id));

    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery))
      )
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      )
    );

    const schoolClassQuery = { sort: 'grade' };
    this.schoolClasses$ = from(
      this.schoolClassService.ensureLoaded(schoolClassQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.schoolClassService.allByQuery(schoolClassQuery))
      )
    );

    this.editor$.subscribe(editor => {
      if (editor && editor.role === EditorRole.ZAVAdmin) {
        this.isZavAdmin = true;
      }
    });

    this.selectedConsumerName$ = this.form.get('consumer')!.valueChanges.pipe(
      startWith(this.form.get('consumer')!.value),
      switchMap(val => {
        const id = Array.isArray(val) ? val[0] : val;
        if (!id) return of('Metadaten');
        return this.store.select(ConsumerEntityState.byId).pipe(
          map(fn => fn(id)),
          map(consumer => consumer?.name || 'Metadaten')
        );
      })
    );

    // Watch consumer changes to detect ABU *** (disabled for now)
    // this.form.get('consumer')!.valueChanges.pipe(
    //   startWith(this.form.get('consumer')!.value),
    //   takeUntilDestroyed(this.destroyRef)
    // ).subscribe(val => {
    //   const id = Array.isArray(val) ? val[0] : val;
    //   if (!id) {
    //     this.isAbuConsumer = false;
    //     return;
    //   }
    //   const byIdFn = this.store.selectSnapshot(ConsumerEntityState.byId);
    //   const consumer = byIdFn(id);
    //   this.isAbuConsumer = consumer?.name === 'ABU';
    //   if (this.isAbuConsumer) {
    //     this.loadAbuMetadata();
    //   }
    // });
  }

  private async loadAbuMetadata(): Promise<void> {
    // Load GlobalMetadata for professions and cantons
    const professionsQuery = { 'filter[name]': 'professions_abu' };
    const cantonsQuery = { 'filter[name]': 'kantone' };

    await this.globalMetadataService.ensureLoaded(professionsQuery);
    await this.globalMetadataService.ensureLoaded(cantonsQuery);

    const professionsMeta = this.store.selectSnapshot(this.globalMetadataService.allByQuery(professionsQuery)) as GlobalMetaDataEntity[];
    const cantonsMeta = this.store.selectSnapshot(this.globalMetadataService.allByQuery(cantonsQuery)) as GlobalMetaDataEntity[];

    // Extract flat profession options from hierarchical structure
    this.professionOptions = [];
    if (professionsMeta.length > 0 && professionsMeta[0].fieldConfig) {
      for (const category of professionsMeta[0].fieldConfig) {
        if (category.subFieldConf) {
          for (const profession of category.subFieldConf) {
            this.professionOptions.push(profession);
          }
        }
      }
    }

    // Extract canton options
    this.cantonOptions = [];
    if (cantonsMeta.length > 0 && cantonsMeta[0].fieldConfig) {
      for (const field of cantonsMeta[0].fieldConfig) {
        if (field.subFieldConf && field.subFieldConf.length > 0) {
          this.cantonOptions = field.subFieldConf;
        }
      }
    }

    // Build select options with id property for MultiComboEntitySearchComponent
    this.cantonSelectOptions = this.cantonOptions.map(c => ({ id: c.name, label: c.label || c.name }));
    this.professionSelectOptions = this.professionOptions.map(p => ({ id: p.name, label: p.label || p.name }));

    // Load existing metadata entries for this module
    if (this.data?.id) {
      const metadataQuery = { 'filter[module]': this.data.id, 'include': 'learningUnit' };
      await this.metadataEntityService.ensureLoaded(metadataQuery);
      const existingMetadata = this.store.selectSnapshot(
        this.metadataEntityService.allByQuery(metadataQuery)
      ) as ModuleLearningUnitMetadataEntity[];

      // Group by canton+profession to build rows
      const rowMap = new Map<string, MetadataRow>();
      for (const entry of existingMetadata) {
        const key = `${entry.cantonKey}::${entry.professionKey}`;
        if (!rowMap.has(key)) {
          rowMap.set(key, {
            cantonKey: entry.cantonKey,
            professionKey: entry.professionKey,
            learningUnitIds: [],
          });
        }
        const luId = this.extractRelationId(entry.learningUnit);
        if (luId) {
          rowMap.get(key)!.learningUnitIds.push(luId);
        }
      }
      this.metadataRows = Array.from(rowMap.values());

      // Initialize per-row state
      for (let i = 0; i < this.metadataRows.length; i++) {
        this.initMetadataRowLearningUnitState(i);
        this.initMetadataRowCantonProfessionState(i);
      }
    }

    // Load all learning units for the metadata selectors
    const allLuQuery = { 'page[size]': 100, 'sort': 'title' };
    await this.learningUnitService.ensureLoaded(allLuQuery);
    this.allLearningUnits = this.store.selectSnapshot(
      this.learningUnitService.allByQuery(allLuQuery)
    ) as LearningUnitEntity[];

    this.cdr.detectChanges();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private extractRelationId(relation: any): string | null {
    if (!relation) return null;
    if (typeof relation === 'string') return relation;
    if (typeof relation === 'object' && 'id' in relation) return relation.id;
    return null;
  }

  addMetadataRow(): void {
    this.metadataRows = [...this.metadataRows, {
      cantonKey: '',
      professionKey: '',
      learningUnitIds: [],
    }];
    const index = this.metadataRows.length - 1;
    this.initMetadataRowLearningUnitState(index);
    this.initMetadataRowCantonProfessionState(index);
  }

  removeMetadataRow(index: number): void {
    this.metadataRows = this.metadataRows.filter((_, i) => i !== index);
    this.metadataRowLearningUnits$.splice(index, 1);
    this.metadataRowSelectedLearningUnits.splice(index, 1);
    this.metadataRowSearchQueries.splice(index, 1);
    this.metadataRowCurrentPages.splice(index, 1);
    this.metadataRowSelectedCantons.splice(index, 1);
    this.metadataRowSelectedProfessions.splice(index, 1);
    this.metadataRowCantonOptions$.splice(index, 1);
    this.metadataRowProfessionOptions$.splice(index, 1);
  }

  updateMetadataRowCanton(index: number, value: string): void {
    this.metadataRows[index] = { ...this.metadataRows[index], cantonKey: value };
  }

  updateMetadataRowProfession(index: number, value: string): void {
    this.metadataRows[index] = { ...this.metadataRows[index], professionKey: value };
  }

  receiveMetadataRowCantonData(index: number, selected: MetadataSelectOption[] | null): void {
    this.metadataRowSelectedCantons[index] = selected ?? [];
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      cantonKey: selected?.[0]?.id ?? '',
    };
  }

  receiveMetadataRowProfessionData(index: number, selected: MetadataSelectOption[] | null): void {
    this.metadataRowSelectedProfessions[index] = selected ?? [];
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      professionKey: selected?.[0]?.id ?? '',
    };
  }

  getMetadataRowCantonLabel(index: number): string {
    const selected = this.metadataRowSelectedCantons[index];
    if (!selected || !selected.length) {
      return 'Kanton';
    }
    return selected.map(c => c.label).join(', ');
  }

  getMetadataRowProfessionLabel(index: number): string {
    const selected = this.metadataRowSelectedProfessions[index];
    if (!selected || !selected.length) {
      return 'Beruf';
    }
    return selected.map(p => p.label).join(', ');
  }

  updateMetadataRowLearningUnits(index: number, value: string[]): void {
    this.metadataRows[index] = { ...this.metadataRows[index], learningUnitIds: value };
  }

  reOrderMetadataRowLearningUnits(index: number, event: CdkDragDrop<LearningUnitEntity[]>): void {
    moveItemInArray(this.metadataRowSelectedLearningUnits[index], event.previousIndex, event.currentIndex);
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      learningUnitIds: this.metadataRowSelectedLearningUnits[index].map(lu => lu.id),
    };
  }

  receiveMetadataRowLearningUnitData(index: number, selected: LearningUnitEntity[] | null): void {
    this.metadataRowSelectedLearningUnits[index] = selected ?? [];
    this.metadataRows[index] = {
      ...this.metadataRows[index],
      learningUnitIds: (selected ?? []).map(lu => lu.id),
    };
  }

  async receiveMetadataRowLearningUnitQuery(index: number, query: string): Promise<void> {
    this.metadataRowSearchQueries[index] = query?.trim() || null;

    if (this.metadataRowSearchQueries[index] && this.metadataRowSearchQueries[index]!.length > 3) {
      const searchParams = { 'filter[titleLike]': this.metadataRowSearchQueries[index]! };
      await this.learningUnitService.ensureLoaded(searchParams);
      this.metadataRowLearningUnits$[index] = this.store.select(this.learningUnitService.allByQuery(searchParams));
    }
    else {
      const pageQuery = { 'page[size]': this.metadataRowPageSize, 'page[number]': 1, 'sort': 'title' };
      this.metadataRowLearningUnits$[index] = this.store.select(this.learningUnitService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [...this.metadataRowSelectedLearningUnits[index], ...paginated]
            .filter((lu, i, self) => i === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreMetadataRowLearningUnits(index: number, event: { pageNumber: number, pageSize: number }): Promise<void> {
    if (!this.metadataRowSearchQueries[index]) {
      const pageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };
      await this.learningUnitService.ensureLoaded(pageQuery);

      const currentLus = await firstValueFrom(this.metadataRowLearningUnits$[index] ?? of([]));
      this.metadataRowLearningUnits$[index] = this.store.select(this.learningUnitService.allByQuery(pageQuery)).pipe(
        map(paginated => {
          return [
            ...this.metadataRowSelectedLearningUnits[index],
            ...currentLus,
            ...paginated,
          ].filter((lu, i, self) => i === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  getMetadataRowLearningUnitLabel(index: number): string {
    const selected = this.metadataRowSelectedLearningUnits[index];
    if (!selected || !selected.length) {
      return 'Lerneinheiten';
    }
    return selected.map(lu => lu.title).join(', ');
  }

  receiveMetadataRowCantonQuery(index: number, query: string): void {
    const search = query?.trim().toLowerCase() || '';
    const filtered = search ?
      this.cantonSelectOptions.filter(c => c.label.toLowerCase().includes(search)) :
      this.cantonSelectOptions;
    this.metadataRowCantonOptions$[index] = of(filtered);
  }

  receiveMetadataRowProfessionQuery(index: number, query: string): void {
    const search = query?.trim().toLowerCase() || '';
    const filtered = search ?
      this.professionSelectOptions.filter(p => p.label.toLowerCase().includes(search)) :
      this.professionSelectOptions;
    this.metadataRowProfessionOptions$[index] = of(filtered);
  }

  private initMetadataRowCantonProfessionState(index: number): void {
    const row = this.metadataRows[index];
    this.metadataRowSelectedCantons[index] = row.cantonKey ?
      this.cantonSelectOptions.filter(c => c.id === row.cantonKey) :
      [];
    this.metadataRowSelectedProfessions[index] = row.professionKey ?
      this.professionSelectOptions.filter(p => p.id === row.professionKey) :
      [];
    this.metadataRowCantonOptions$[index] = of(this.cantonSelectOptions);
    this.metadataRowProfessionOptions$[index] = of(this.professionSelectOptions);
  }

  private initMetadataRowLearningUnitState(index: number): void {
    const row = this.metadataRows[index];
    const state = this.store.selectSnapshot(this.learningUnitService.stateClass) as EntityStateModel<LearningUnitEntity>;
    this.metadataRowSelectedLearningUnits[index] = row.learningUnitIds
      .map(id => state.entities[id])
      .filter((e): e is LearningUnitEntity => !!e);
    this.metadataRowSearchQueries[index] = null;
    this.metadataRowCurrentPages[index] = 1;

    const pageQuery = { 'page[size]': this.metadataRowPageSize, 'page[number]': 1, 'sort': 'title' };
    this.metadataRowLearningUnits$[index] = this.store.select(this.learningUnitService.allByQuery(pageQuery)).pipe(
      map(paginated => {
        return [...this.metadataRowSelectedLearningUnits[index], ...paginated]
          .filter((lu, i, self) => i === self.findIndex(s => s.id === lu.id));
      })
    );
  }

  getCantonLabel(key: string): string {
    return this.cantonOptions.find(c => c.name === key)?.label || key;
  }

  getProfessionLabel(key: string): string {
    return this.professionOptions.find(p => p.name === key)?.label || key;
  }

  getLearningUnitTitle(id: string): string {
    return this.allLearningUnits.find(lu => lu.id === id)?.title || id;
  }

  receiveLearningUnitData(selected: LearningUnitEntity[] | null) {
    this.selectedLearningUnits = selected ?? [];
    this.form.get('learningUnits')?.setValue(this.selectedLearningUnits.map(s => s.id));
  }

  async receiveLearningUnitQuery(query: string) {
    this.searchQueryLearningUnits = query?.trim() || null;

    if (this.searchQueryLearningUnits && this.searchQueryLearningUnits.length > 3) {
      const searchParams = { 'filter[titleLike]': this.searchQueryLearningUnits };
      await this.learningUnitService.ensureLoaded(searchParams);

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(searchParams));
    }
    else {
      const learningUnitPageQuery = { 'page[size]': this.pageSizeLearningUnits, 'page[number]': this.currentPageLearningUnits, 'sort': 'title' };
      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated => {
          return [...this.selectedLearningUnits, ...paginated]
            .filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  async loadMoreLearningUnits(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningUnits) {
      const learningUnitPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.learningUnitService.ensureLoaded(learningUnitPageQuery);

      const currentLearningUnits = await firstValueFrom(this.learningUnits$ ?? of([]));

      this.learningUnits$ = this.store.select(this.learningUnitService.allByQuery(learningUnitPageQuery)).pipe(
        map(paginated => {
          return [
            ...this.selectedLearningUnits,
            ...currentLearningUnits,
            ...paginated,
          ].filter((lu, index, self) => index === self.findIndex(s => s.id === lu.id));
        })
      );
    }
  }

  reOrderSelectedLearningUnits(event: CdkDragDrop<LearningUnitEntity[]>) {
    moveItemInArray(this.selectedLearningUnits, event.previousIndex, event.currentIndex);
    this.form.get('learningUnits')?.setValue(this.selectedLearningUnits.map(lu => lu.id));
  }

  public get learningUnitLabel(): string {
    if (!this.selectedLearningUnits || !this.selectedLearningUnits.length) {
      return 'Lerneinheiten';
    }
    return this.selectedLearningUnits.map(lu => lu.title).join(', ');
  }

  public openImageChoice(): void {
    this.showMediumDialog('Image');
  }

  public showMediumDialog(mediaType: MediaType): void {
    const chooseMedium: ChooseMedium = {
      mediaType: mediaType,
      desktopAudio: false,
      subjectId: ''
    };

    const dialogRef = this.dialog.open(ChooseMediumComponent, {
      minWidth: '60vw',
      maxWidth: '80vw',
      minHeight: '530px',
      maxHeight: '530px',
      data: chooseMedium
    });

    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((answer: AssetJsonModel) => {
        if (!answer) return;

        const previewControl = this.form.get('preview');

        if (!previewControl?.value || Array.isArray(previewControl.value)) {
          // initialize as object if empty
          previewControl?.setValue({ id: '', name: '', mediaType: '', media_url: '' });
        }

        // update the form control value
        previewControl?.patchValue({
          id: answer.id,
          mediaType: 'Image',
          media_url: answer.media_url,
          name: answer.name
        });

        this.cdr.detectChanges();
        this.buildURLs();
      });
  }

  private buildURLs(): void {
    const baseUrl = environment.isProd ? '/' : 'https://zav-stage.westermann.de/';

    const preview = this.form.get('preview')?.value;

    if (preview && preview.media_url) {
      this.imageUrl.set(baseUrl + preview.media_url);
    }
  }

  public deleteMediaEntry(): void {
    const previewControl = this.form.get('preview');
    const preview = previewControl?.value;

    const id = preview?.id;
    const name = preview?.name;
    const headerText = 'Bild-Datei löschen';

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        id,
        name,
        itemType: 'Datei',
        headerText,
        deleteFn: () => {
          // clear the form control instead of this.data.preview
          previewControl?.setValue({ id: '', name: '', mediaType: '', media_url: '' });
          this.cdr.detectChanges();
          dialogRef.close();
        }
      },
    });
  }

  public getImageName(): string {
    const preview = this.form.get('preview')?.value;
    return preview?.name || '';
  }

  get contentControls(): FormArray {
    return this.form.get('content') as FormArray;
  }

  addContent() {
    this.contentControls.push(this.fb.control(''));
    this.cdr.detectChanges();
  }

  statusChanged(newStatus: string) {
    this.status = newStatus;
  }

  removeContent(index: number) {
    this.contentControls.removeAt(index);
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.value;

      const updatedModule = Object.assign(
        new ModuleEntity(),
        { id: this.data?.id },
        formValue,
        {
          consumer: formValue.consumer ? { type: 'consumers', id: formValue.consumer } : null,
          subjects: (formValue.subjects as string[])?.length ?
            (formValue.subjects as string[]).map(id => ({ id })) :
            [],
          schoolClasses: (formValue.schoolClasses as string[])?.length ?
            (formValue.schoolClasses as string[]).map(id => ({ id })) :
            [],
          learningUnits: (formValue.learningUnits as string[])?.length ?
            (formValue.learningUnits as string[]).map(id => ({ id })) :
            [],
        }
      );

      // Attach metadata rows for the caller to handle
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (updatedModule as any)._metadataRows = this.isAbuConsumer ? this.metadataRows : undefined;

      this.dialogRef.close(updatedModule);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackById(index: number, item: LearningUnitEntity) {
    return item.id;
  }
}
